import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { adminApi } from "../../lib/adminApi";
import { logger } from "../../lib/logger";

/**
 * Wraps every /admin/* route except /admin/login.
 *
 * On mount: GET /api/admin/me.
 *   - 200 → authenticated → render children.
 *   - 401 → not authenticated → redirect to /admin/login (preserve `from`).
 *
 * The HMAC-signed sf_admin cookie is httpOnly — we NEVER read it from JS.
 * Auth state is derived only from the /me response.
 */
export default function AdminAuthGuard({ children }) {
  const location = useLocation();
  const [state, setState] = useState({ status: "checking", admin: null });

  // Hoist the redirect state to a memoized object so a fresh reference
  // isn't created on each render (Navigate is a terminal redirect, but the
  // rule applies uniformly across the codebase).
  const redirectState = useMemo(
    () => ({ from: location.pathname }),
    [location.pathname],
  );

  useEffect(() => {
    let cancelled = false;
    adminApi
      .get("/admin/me")
      .then((res) => {
        if (cancelled) return;
        setState({ status: "ok", admin: res.data });
      })
      .catch((err) => {
        if (cancelled) return;
        logger.error("admin_me_failed", { status: err?.response?.status });
        setState({ status: "unauth", admin: null });
      });
    return () => { cancelled = true; };
  }, [location.pathname]);

  if (state.status === "checking") {
    return (
      <div className="sf-container" style={{ padding: "80px 24px" }} data-testid="admin-guard-checking">
        <div className="sf-muted-text">Verifying admin session…</div>
      </div>
    );
  }
  if (state.status === "unauth") {
    return <Navigate to="/admin/login" state={redirectState} replace />;
  }
  return typeof children === "function" ? children(state.admin) : children;
}
